/*
Card detail page. Renders the decoded view (one section per family —
SELECT, SV, environment, contracts, events) and the raw metadata JSON
in a collapsible block. The list of bundle parts attached to the card
is also shown so admins can see what was uploaded, with a download
button per part wired to GET /api/contents/:id/parts/:name.

When the authenticated caller is the card's owner, an extra
"Partage" section surfaces a "Partager" dialog (POST /share) and a
table of currently-active recipients with revoke buttons (DELETE
/share?recipientEmail=…).
*/

import { useCallback, useEffect, useState, type FormEvent, type JSX } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api, apiBlob, triggerDownload } from "../lib/api";
import { useAuth } from "../lib/auth";
import { describeSelectField } from "../lib/startupInfoLookup";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { ErrorState } from "../components/ErrorState";
import { Skeleton } from "../components/Skeleton";

interface DecodedField
{
	name:	string;	// Technical field name
	label:	string;	// Display label
	raw:	string;	// Raw hex value
	value:	string;	// Decoded textual value
	status:	string;	// VALID | WARNING | INVALID
}

interface DecodedBlock
{
	index?:		number;			// 1-based index for repeated entries
	fields:		DecodedField[];	// Decoded field list
}

interface DecodedView
{
	select?:		DecodedField[];	// SELECT APPLICATION fields
	sv?:			DecodedField[];	// SV Get for Load fields
	environment?:	DecodedField[];	// Environment fields
	contracts?:		DecodedBlock[];	// One entry per contract
	events?:		DecodedBlock[];	// One entry per event
}

interface CardPart
{
	id:				string;	// Part identifier
	name:			string;	// metadata, raw, select-fci, …
	filename:		string | null;
	contentType:	string;
	createdAt:		number;
}

interface CardDetail
{
	id:				string;
	userId:			string;
	serialNumber:	string;
	cardType:		string;
	dataModel:		string;
	readDate:		number;
	folderPath:		string;
	metadata:		unknown;
	decoded:		DecodedView | null;
	parts:			CardPart[];
	createdAt:		number;
	updatedAt:		number;
}

export function CardDetailPage(): JSX.Element
{
	const { id: vId = "" }		= useParams();
	const { t }					= useTranslation("card_detail");
	const { t: tc }				= useTranslation("common");
	const [fCard, fSetCard]		= useState<CardDetail | null>(null);
	const [fError, fSetError]	= useState<string | null>(null);

	const fLoad = useCallback(() =>
	{
		fSetError(null);
		api<CardDetail>("GET", `/contents/${encodeURIComponent(vId)}`)
			.then(fSetCard)
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : tc("error_network"));
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vId]);

	useEffect(() => { fLoad(); }, [fLoad]);

	if (fError !== null)
	{
		return <ErrorState message={fError} onRetry={fLoad} />;
	}

	if (fCard === null)
	{
		return (
			<div className="card">
				<Skeleton variant="title" widthPercent={50} />
				<Skeleton variant="text"  widthPercent={70} />
				<div style={{ height: "1rem" }} />
				<Skeleton variant="row" widthPercent={90} />
				<Skeleton variant="row" widthPercent={80} />
				<Skeleton variant="row" widthPercent={85} />
				<Skeleton variant="row" widthPercent={75} />
			</div>
		);
	}

	const vDecoded = fCard.decoded ?? null;	// Decoded view shorthand

	return (
		<>
			<p className="muted card-detail__back"><Link to="/cards">{t("back_to_list")}</Link></p>

			<div className="card">
				<div className="card-detail__header">
					<div className="card-detail__heading">
						<h2>{fCard.serialNumber}</h2>
						<p className="muted">
							{t("read_on", { type: fCard.cardType, model: fCard.dataModel, date: formatDateTime(fCard.readDate) })}
						</p>
					</div>
					<div className="card-detail__actions">
						<CompareSection card={fCard} />
						<DownloadsSection card={fCard} />
					</div>
				</div>
			</div>

			{vDecoded?.select !== undefined ? <SectionFields title="SELECT" fields={decorateSelectFields(vDecoded.select)} /> : null}
			{vDecoded?.sv     !== undefined ? <SectionFields title="SV"     fields={vDecoded.sv}     /> : null}
			{vDecoded?.environment !== undefined ? <SectionFields title="Environnement" fields={vDecoded.environment} /> : null}
			{vDecoded?.contracts !== undefined ? <SectionBlocks title="Contrats" blocks={vDecoded.contracts} /> : null}
			{vDecoded?.events    !== undefined ? <SectionBlocks title="Événements" blocks={vDecoded.events}    /> : null}

			<ShareSection card={fCard} />

			{fCard.parts.length > 0
				? (
					<div className="card">
						<h2>{t("parts_bundle_title")}</h2>
						<table>
							<thead>
								<tr>
									<th>{t("parts_col_name")}</th>
									<th>{t("parts_col_type")}</th>
									<th>{t("parts_col_file")}</th>
									<th>{t("parts_col_received")}</th>
								</tr>
							</thead>
							<tbody>
								{fCard.parts.map((p) => (
									<tr key={p.id}>
										<td>{p.name}</td>
										<td>{p.contentType}</td>
										<td>{p.filename ?? tc("dash")}</td>
										<td>{formatDateTime(p.createdAt)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)
				: null}

			<div className="card">
				<h2>{t("raw_metadata_title")}</h2>
				<pre className="json-block">{JSON.stringify(fCard.metadata, null, 2)}</pre>
			</div>
		</>
	);
}

/*
One downloadable entry derived from a card. `path` is the URL after
/api (no leading "/api"). `suggestedName` is the fallback filename
used when the server's Content-Disposition header doesn't carry one.
`origin` distinguishes parts the client uploaded explicitly (stored
CardPart row) from those reconstructed server-side from metadata
base64 fields (the §5.2 fallback) — surfaced as a small badge so the
admin knows the difference.
*/
interface DownloadEntry
{
	label:			string;	// Human-readable button label
	path:			string;	// /contents/<id>/parts/<name>[?index=N]
	suggestedName:	string;	// Fallback filename
	origin:			"stored" | "derived";
}

/*
Renders the download bar. One button per stored CardPart row; for
the four singletons that have a JSON-fallback path on the server
(raw, select-fci, sv-get, decoded), a "(dérivé)" button is added
when no stored CardPart exists for that name but the metadata
provides one. metadata itself is intentionally not exposed as a
download — the same JSON is already visible in the "Métadonnée
brute" panel below.
*/
function DownloadsSection(
	props: { card: CardDetail }
): JSX.Element
{
	const { t }		= useTranslation("card_detail");
	const { t: tc }	= useTranslation("common");

	const [fBusyKey, fSetBusyKey] = useState<string | null>(null);

	const vEntries = buildDownloadEntries(props.card);	// Combined stored + derived

	if (vEntries.length === 0)
	{
		return <></>;
	}

	const fOnClick = async (inEntry: DownloadEntry) =>
	{
		fSetBusyKey(inEntry.path);

		try
		{
			const { blob: vBlob, filename: vFilename } = await apiBlob(inEntry.path);

			triggerDownload(vBlob, vFilename ?? inEntry.suggestedName);
		}
		catch (vErr)
		{
			alert(vErr instanceof ApiError
				? t("downloads_error_status", { status: vErr.status, message: vErr.message })
				: t("downloads_error_network"));
		}
		finally
		{
			fSetBusyKey(null);
		}
	};

	return (
		<>
			{vEntries.map((e) => (
				<button
					key={e.path}
					type="button"
					className="button button--ghost"
					disabled={fBusyKey === e.path}
					onClick={() => fOnClick(e)}
				>
					{fBusyKey === e.path ? tc("downloading") : e.label}
					{e.origin === "derived" ? <span className="muted">&nbsp;{t("downloads_derived")}</span> : null}
				</button>
			))}
		</>
	);
}

/*
Computes the list of downloadable entries for a card. Stored
CardPart rows come first (one button each, with ?index= appended
when the same name repeats — ef-record / attachment). Derived
fallbacks are added afterwards for raw / select-fci / sv-get /
decoded when the metadata carries the corresponding base64 / object
and no stored row of that name exists.
*/
/*
One row in the picker that lets the user pick a second card to
compare with. Same compact shape as the /api/contents list items.
*/
interface CompareCandidate
{
	id:				string;
	serialNumber:	string;
	cardType:		string;
	dataModel:		string;
	readDate:		number;
}

interface CompareCandidatesResponse
{
	items:		CompareCandidate[];
	total:		number;
	page:		number;
	pageSize:	number;
}

/*
"Comparer cette carte avec…" — a small section with a single
button that opens a modal listing the user's other cards. Picking
a row navigates to /cards/compare?a=current&b=picked. Available
on every card detail page (owner, admin, sharee — anyone who can
read the card can run a comparison against another card they can
read).
*/
function CompareSection(
	props: { card: CardDetail }
): JSX.Element
{
	const { t }		= useTranslation("compare");
	const fNavigate	= useNavigate();

	const [fOpen,		fSetOpen]		= useState<boolean>(false);
	const [fCandidates,	fSetCandidates]	= useState<CompareCandidate[] | null>(null);
	const [fError,		fSetError]		= useState<string | null>(null);
	const [fSearch,		fSetSearch]		= useState<string>("");

	useEffect(() =>
	{
		if (!fOpen)
		{
			return;
		}

		fSetCandidates(null);
		fSetError(null);

		const vParams = new URLSearchParams();
		vParams.set("page",     "1");
		vParams.set("pageSize", "200");

		if (fSearch.trim() !== "")
		{
			vParams.set("serialNumber", fSearch.trim());
		}

		api<CompareCandidatesResponse>("GET", `/contents?${vParams.toString()}`)
			.then((vResp) =>
			{
				// Drop the current card itself — comparing a card
				// with itself is a no-op everywhere.
				fSetCandidates(vResp.items.filter((c) => c.id !== props.card.id));
			})
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : "");
			});
	}, [fOpen, fSearch, props.card.id]);

	const fOnPick = (inOther: CompareCandidate) =>
	{
		fSetOpen(false);
		fNavigate(`/cards/compare?a=${encodeURIComponent(props.card.id)}&b=${encodeURIComponent(inOther.id)}`);
	};

	return (
		<>
			<button type="button" className="button button--ghost" onClick={() => fSetOpen(true)}>
				{t("button")}
			</button>

			{fOpen
				? (
					<div className="modal-backdrop" onClick={() => fSetOpen(false)} role="presentation">
						<div className="modal-dialog compare-picker" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
							<h2 style={{ marginTop: 0 }}>{t("picker_title")}</h2>
							<input
								type="text"
								placeholder={t("picker_search")}
								value={fSearch}
								onChange={(e) => fSetSearch(e.target.value)}
								aria-label={t("picker_search")}
								autoFocus
							/>
							<div className="compare-picker__list">
								{fError !== null
									? <p className="error">{fError}</p>
									: fCandidates === null
										? <Skeleton variant="row" widthPercent={90} />
										: fCandidates.length === 0
											? <p className="muted">{t("picker_empty")}</p>
											: fCandidates.map((c) => (
												<button
													key={c.id}
													type="button"
													className="compare-picker__row"
													onClick={() => fOnPick(c)}
												>
													<code>{c.serialNumber}</code>
													<span className="muted"> — {c.cardType} / {c.dataModel}</span>
												</button>
											))}
							</div>
							<div className="modal-dialog__buttons">
								<button type="button" className="button button--ghost" onClick={() => fSetOpen(false)}>
									{t("picker_cancel")}
								</button>
							</div>
						</div>
					</div>
				)
				: null}
		</>
	);
}

/*
One row in the active-shares table.
*/
interface ShareRow
{
	id:				string;	// Sharing row id
	recipientEmail:	string;	// To whom
	message:		string | null;	// Optional note
	createdAt:		number;	// When shared
	viewedAt:		number | null;	// First-view timestamp; null = unread
}

/*
Owner-only section: a "Partager" dialog and a table of currently-
active recipients with revoke buttons. Renders nothing when the
authenticated caller isn't the owner of the card (read-only access
via Sharing or admin role doesn't expose the recipient list).
*/
function ShareSection(
	props: { card: CardDetail }
): JSX.Element
{
	const { userId: vMeId } = useAuth();
	const vIsOwner	= vMeId === props.card.userId;	// True when the caller owns this card
	const fConfirm	= useConfirmDialog();
	const { t }		= useTranslation("card_detail");
	const { t: tc }	= useTranslation("common");

	const [fShares,    fSetShares]		= useState<ShareRow[] | null>(null);
	const [fError,     fSetError]		= useState<string | null>(null);
	const [fEmail,     fSetEmail]		= useState<string>("");
	const [fMessage,   fSetMessage]		= useState<string>("");
	const [fBusy,      fSetBusy]		= useState<boolean>(false);
	const [fRevokingId, fSetRevokingId]	= useState<string | null>(null);

	const fLoad = useCallback(() =>
	{
		if (!vIsOwner)
		{
			return;
		}

		api<ShareRow[]>("GET", `/contents/${encodeURIComponent(props.card.id)}/shares`)
			.then(fSetShares)
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : tc("error_network"));
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.card.id, vIsOwner]);

	useEffect(() => { fLoad(); }, [fLoad]);

	if (!vIsOwner)
	{
		return <></>;
	}

	const fHandleSubmit = async (inEvent: FormEvent<HTMLFormElement>) =>
	{
		inEvent.preventDefault();
		fSetError(null);
		fSetBusy(true);

		try
		{
			await api(
				"POST",
				`/contents/${encodeURIComponent(props.card.id)}/share`,
				{ recipientEmail: fEmail.trim(), message: fMessage.trim().length > 0 ? fMessage.trim() : undefined }
			);
			fSetEmail("");
			fSetMessage("");
			fLoad();
		}
		catch (vErr)
		{
			fSetError(translateShareError(t, tc, vErr));
		}
		finally
		{
			fSetBusy(false);
		}
	};

	const fHandleRevoke = async (inEntry: ShareRow) =>
	{
		const vOk = await fConfirm.confirm(
			{
				title:			t("revoke_dialog_title"),
				entity:			inEntry.recipientEmail,
				message:		t("revoke_dialog_body"),
				confirmLabel:	t("revoke_button"),
			}
		);

		if (!vOk)
		{
			return;
		}

		fSetRevokingId(inEntry.id);

		try
		{
			await api<void>(
				"DELETE",
				`/contents/${encodeURIComponent(props.card.id)}/share?recipientEmail=${encodeURIComponent(inEntry.recipientEmail)}`
			);
			fLoad();
		}
		catch (vErr)
		{
			alert(vErr instanceof ApiError ? vErr.message : t("revoke_error"));
		}
		finally
		{
			fSetRevokingId(null);
		}
	};

	return (
		<div className="card">
			<h2>{t("share_title")}</h2>

			<form onSubmit={fHandleSubmit} className="share-form">
				<input
					type="email"
					placeholder={t("share_email_placeholder")}
					value={fEmail}
					onChange={(e) => fSetEmail(e.target.value)}
					required
					aria-label={t("share_email_placeholder")}
				/>
				<input
					type="text"
					placeholder={t("share_message_placeholder")}
					value={fMessage}
					onChange={(e) => fSetMessage(e.target.value)}
					maxLength={4096}
					aria-label={t("share_message_placeholder")}
				/>
				<button type="submit" className="button" disabled={fBusy}>
					{fBusy ? t("share_progress") : t("share_submit")}
				</button>
			</form>

			{fError !== null ? <div className="error">{fError}</div> : null}

			{fShares !== null && fShares.length > 0
				? (
					<table style={{ marginTop: "0.75rem" }}>
						<thead>
							<tr>
								<th>{t("shares_col_recipient")}</th>
								<th>{t("shares_col_shared_on")}</th>
								<th>{t("shares_col_viewed")}</th>
								<th>{t("shares_col_message")}</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{fShares.map((s) => (
								<tr key={s.id}>
									<td>{s.recipientEmail}</td>
									<td>{formatDateTime(s.createdAt)}</td>
									<td>{s.viewedAt !== null ? formatDateTime(s.viewedAt) : <span className="muted">{tc("never")}</span>}</td>
									<td className="muted">{s.message ?? tc("dash")}</td>
									<td style={{ whiteSpace: "nowrap" }}>
										<button
											type="button"
											className="button button--danger"
											disabled={fRevokingId === s.id}
											onClick={() => fHandleRevoke(s)}
										>
											{fRevokingId === s.id ? t("revoke_progress") : t("revoke_button")}
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)
				: <p className="muted" style={{ marginTop: "0.75rem" }}>{t("no_active_shares")}</p>}
			{fConfirm.dialog}
		</div>
	);
}

/*
Translates ApiError codes from the share endpoint to a user-facing
message via the i18n card_detail namespace.
*/
function translateShareError(
	inT:	import("i18next").TFunction,	// useTranslation("card_detail").t
	inTc:	import("i18next").TFunction,	// useTranslation("common").t
	inErr:	unknown
): string
{
	if (inErr instanceof ApiError)
	{
		switch (inErr.code)
		{
			case "missing_field":			return inT("share_err_missing_field");
			case "recipient_not_found":		return inT("share_err_recipient");
			case "cannot_share_with_self":	return inT("share_err_self");
			case "forbidden":				return inT("share_err_forbidden");
			default:						return inT("share_err_default", { status: inErr.status });
		}
	}

	return inTc("error_network");
}

function buildDownloadEntries(
	inCard:	CardDetail
): DownloadEntry[]
{
	const vEntries:	DownloadEntry[]		= [];	// Output list
	const vCounts:	Record<string, number>	= {};	// Per-name running index

	for (const vPart of inCard.parts)
	{
		if (vPart.name === "metadata")
		{
			continue;	// Same JSON as the "Métadonnée brute" panel — skip
		}

		const vIdx = vCounts[vPart.name] ?? 0;

		vCounts[vPart.name] = vIdx + 1;

		// Find total count of this name across the card so we know whether
		// to suffix ?index=. Filter once is fine — parts arrays are small.
		const vTotalSameName = inCard.parts.filter((p) => p.name === vPart.name).length;
		const vNeedsIndex	= vTotalSameName > 1;
		const vPath			= vNeedsIndex
			? `/contents/${encodeURIComponent(inCard.id)}/parts/${encodeURIComponent(vPart.name)}?index=${vIdx}`
			: `/contents/${encodeURIComponent(inCard.id)}/parts/${encodeURIComponent(vPart.name)}`;

		const vLabel = vPart.filename !== null
			? `${vPart.name} — ${vPart.filename}`
			: vNeedsIndex
				? `${vPart.name} #${vIdx + 1}`
				: vPart.name;

		vEntries.push(
			{
				label:			vLabel,
				path:			vPath,
				suggestedName:	vPart.filename ?? `${inCard.serialNumber}-${vPart.name}.bin`,
				origin:			"stored",
			}
		);
	}

	// Derived fallbacks for the four singletons covered by §5.2.
	const vMeta		= (inCard.metadata ?? {}) as Record<string, unknown>;	// metadata blob
	const vHasStored	= (inName: string) => inCard.parts.some((p) => p.name === inName);

	const vDerivedCandidates:	{ name: string; available: boolean; ext: string }[] = [
		{ name: "raw",        available: typeof vMeta["rawData"]           === "string" && (vMeta["rawData"]           as string).length > 0, ext: "bin" },
		{ name: "select-fci", available: typeof vMeta["selectAppResponse"] === "string" && (vMeta["selectAppResponse"] as string).length > 0, ext: "bin" },
		{ name: "sv-get",     available: typeof vMeta["svGetResponse"]     === "string" && (vMeta["svGetResponse"]     as string).length > 0, ext: "bin" },
		{ name: "decoded",    available: inCard.decoded !== null, ext: "json" },
	];

	for (const vCand of vDerivedCandidates)
	{
		if (!vCand.available || vHasStored(vCand.name))
		{
			continue;
		}

		vEntries.push(
			{
				label:			vCand.name,
				path:			`/contents/${encodeURIComponent(inCard.id)}/parts/${encodeURIComponent(vCand.name)}`,
				suggestedName:	`${inCard.serialNumber}-${vCand.name}.${vCand.ext}`,
				origin:			"derived",
			}
		);
	}

	return vEntries;
}

/*
Renders one decoded section as a key/value table.
Status badge mirrors the Android colour code:
  - VALID    : green pill (default; absent when status is not the
               three known values to avoid coloring "OK" rows the
               whole table over)
  - WARNING  : orange pill — value is plausible but flagged
  - INVALID  : red pill — decoder rejected the value outright
*/
function SectionFields(
	props:	{ title: string; fields: DecodedField[] }
): JSX.Element
{
	if (props.fields.length === 0)
	{
		return <></>;
	}

	return (
		<div className="card">
			<h2>{props.title}</h2>
			<table>
				<tbody>
					{props.fields.map((f) => (
						<tr key={f.name}>
							<td style={{ width: "30%", color: "var(--color-muted)" }}>{f.label}</td>
							<td>
								<StatusBadge status={f.status} />
								<span style={{ marginLeft: "0.4rem" }}>{f.value}</span>
								<span className="muted" style={{ marginLeft: "0.5rem", fontFamily: "monospace" }}>{f.raw}</span>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/*
Tiny coloured pill rendering the three known field statuses. Anything
outside of {VALID, WARNING, INVALID} is treated as no-status and the
badge is omitted, so adding a new status value server-side doesn't
make every row glow red until the SPA is updated.
*/
function StatusBadge(
	props: { status: string }
): JSX.Element
{
	const vClass	= props.status === "WARNING"	? "field-status field-status--warning"
				:	  props.status === "INVALID"	? "field-status field-status--invalid"
				:	  props.status === "VALID"		? "field-status field-status--valid"
				:	  null;

	if (vClass === null)
	{
		return <></>;
	}

	return <span className={vClass} title={props.status}>{props.status}</span>;
}

/*
Renders a section made of repeated blocks (contracts, events).
*/
function SectionBlocks(
	props:	{ title: string; blocks: DecodedBlock[] }
): JSX.Element
{
	if (props.blocks.length === 0)
	{
		return <></>;
	}

	return (
		<div className="card">
			<h2>{props.title}</h2>
			{props.blocks.map((b) => (
				<div key={b.index} style={{ marginBottom: "0.75rem" }}>
					<h3 style={{ margin: "0 0 0.4rem 0", fontSize: "0.95rem" }}>
						{props.title} {b.index ?? ""}
					</h3>
					<SectionFields title="" fields={b.fields} />
				</div>
			))}
		</div>
	);
}

/*
Replaces the `value` of every Platform / ApplicationType /
SoftwareIssuer field in the SELECT section with the human-readable
descriptor from CalypsoTN001. Fields whose `name` doesn't match are
returned untouched, so the function is safe to call over the whole
section without filtering.
*/
function decorateSelectFields(
	inFields:	DecodedField[]	// Decoded SELECT fields from the server
): DecodedField[]
{
	return inFields.map((vField) => (
		{
			...vField,
			value:	describeSelectField(vField.name, vField.raw, vField.value),
		}
	));
}

/*
Formats a ms-since-epoch timestamp to "YYYY-MM-DD HH:MM" in the local TZ.
*/
function formatDateTime(
	inMs:	number	// Timestamp in milliseconds
): string
{
	const vDate = new Date(inMs);	// Local Date

	const pad = (n: number) => String(n).padStart(2, "0");
	return `${vDate.getFullYear()}-${pad(vDate.getMonth() + 1)}-${pad(vDate.getDate())} ${pad(vDate.getHours())}:${pad(vDate.getMinutes())}`;
}
